import { describeError, logError, warn } from '../log';
/**
 * Contact-form submission.
 *
 * Ported from the submit path in components/text-widget/text-widget.tsx,
 * which builds the body and posts it inline. A host has no component to do
 * that in.
 *
 * The retry is the important part. The request token is single-use and
 * short-lived, so a form that sat on screen while the visitor typed can
 * arrive with a token the server has already expired or consumed. The server
 * answers 401, and the only thing that helps is fetching a fresh token,
 * waiting out the minimum submit interval, and sending once more. Retrying
 * with the same token, or without the wait, fails the same way.
 *
 * Exactly once. A loop here would be an abuse vector against the endpoint the
 * token exists to protect.
 */

import {
  isRetryableRequestTokenError,
  markRequestTokenConsumed,
  refreshRequestTokenFromOpenApi,
  removeRequestTokenFromSession,
  REQUEST_TOKEN_SUBMIT_DELAY_MS,
} from '../security/request-token';

export interface SubmitFormOptions {
  marketplaceUrl: string;
  locationId: string;
  widgetId?: string;
  chatType: string;
  source: string;
  /** Everything except the token; the token is added and re-added on retry. */
  body: Record<string, unknown>;
  requestToken: string;
  fetchImpl?: typeof fetch;
}

/**
 * The statuses with which the server asks for an interactive captcha
 * challenge. 490 is what the backend source names; staging was observed on
 * 2026-09-11 answering the anti-spam gate with 492 and the same challenge
 * body, so both are read as "pass a check and try again", not as failures.
 *
 * 494 is NOT in this set. The edge answers it with the same body, but the
 * reason is different: the anti-spam layer refused the details themselves,
 * an unformatted phone number being the usual cause (confirmed 2026-09-29).
 * A challenge would only send the visitor round in a circle; the client
 * reports it as `rejected`, and normalises phones so it rarely happens.
 */
export const CAPTCHA_CHALLENGE_STATUS = 490;
export const REJECTED_BY_SPAM_LAYER_STATUS = 494;
export const CAPTCHA_CHALLENGE_STATUSES: ReadonlySet<number> = new Set([490, 492]);
export const isCaptchaChallengeStatus = (status: number | undefined): boolean =>
  status !== undefined && CAPTCHA_CHALLENGE_STATUSES.has(status);

export class FormSubmitError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    /** What the server said, verbatim. For logs, never for a visitor. */
    readonly serverMessage?: string,
  ) {
    super(message);
    this.name = 'FormSubmitError';
  }
}

/**
 * The reason a rejection gives, whatever shape it arrives in.
 *
 * Nest errors are JSON with a `message`. The captcha middleware answers with
 * `res.sendStatus(401)`, which is the bare text "Unauthorized" — so a 401
 * with that body means the captcha token did not verify, and one with a JSON
 * body is about the request token. Parsing only JSON lost that distinction.
 */
async function readServerMessage(response: Response): Promise<string> {
  let raw = '';
  try {
    raw = await response.clone().text();
  } catch (e) {
    warn('could not read the body of a rejected form submit', { reason: describeError(e) });
    return '';
  }
  try {
    const parsed = JSON.parse(raw);
    const message = parsed?.message ?? parsed?.msg ?? parsed?.error;
    return typeof message === 'string' ? message : Array.isArray(message) ? message.join('; ') : raw;
  } catch {
    return raw.trim();
  }
}

/** Voice widgets submit to their own endpoint. */
export function formEndpointFor(chatType: string): string {
  return chatType === 'voiceAiChat' ? '/chat-widget/voiceAi' : '/chat-widget/forms';
}

export async function submitChatWidgetForm(options: SubmitFormOptions): Promise<unknown> {
  const doFetch = options.fetchImpl || fetch;
  const url = options.marketplaceUrl + formEndpointFor(options.chatType);

  const post = (token: string) =>
    doFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...options.body, requestToken: token }),
    });

  /**
   * Burn the token. It is single-use, so a token that reached the server is
   * spent whether the submit succeeded or was rejected — leaving it in
   * session storage means the next submit sends one the server has already
   * consumed and gets a 401 for it.
   */
  const burn = (token: string) => {
    markRequestTokenConsumed(options.locationId, options.widgetId, token);
    removeRequestTokenFromSession(options.locationId, options.widgetId);
  };

  let response = await post(options.requestToken);
  let serverMessage = '';

  if (response.status === 401) {
    const message = await readServerMessage(response);
    serverMessage = message;
    logError('the form endpoint rejected the submit with 401', undefined, {
      serverMessage: message,
      // A bare "Unauthorized" is the captcha middleware: the captchaToken did
      // not pass siteverify. Anything naming requestToken is the token check,
      // which runs after the captcha.
      meaning:
        message === 'Unauthorized'
          ? 'captcha token rejected by the server'
          : isRetryableRequestTokenError(message)
            ? 'request token problem; refreshing and retrying once'
            : 'request token rejected; not retryable',
      hadCaptchaToken: !!options.body?.captchaToken,
    });

    if (isRetryableRequestTokenError(message)) {
      burn(options.requestToken);
      const fresh = await refreshRequestTokenFromOpenApi({
        marketplaceURL: options.marketplaceUrl,
        locationId: options.locationId,
        widgetId: options.widgetId,
        chatType: options.chatType,
        source: options.source,
        reason: 'refresh',
      });

      if (fresh) {
        // The server rejects a submit that lands inside this window, so
        // retrying immediately would trade one rejection for another.
        await new Promise(resolve => setTimeout(resolve, REQUEST_TOKEN_SUBMIT_DELAY_MS));
        response = await post(fresh);
        if (response.ok) burn(fresh);
        else {
          serverMessage = await readServerMessage(response);
          logError('the retry with a fresh request token was rejected too', undefined, {
            status: response.status,
            serverMessage,
          });
        }
      }
    } else {
      burn(options.requestToken);
    }
  } else if (response.ok) {
    burn(options.requestToken);
  }

  if (!response.ok) {
    if (response.status !== 401) {
      serverMessage = await readServerMessage(response);
      // 490 and 429 are handled upstream as a challenge, not a failure.
      if (!isCaptchaChallengeStatus(response.status) && response.status !== 429) {
        logError('the form endpoint rejected the submit', undefined, {
          status: response.status,
          serverMessage,
        });
      }
    }
    throw new FormSubmitError('Form submission failed', response.status, serverMessage);
  }

  try {
    return await response.json();
  } catch (e) {
    // The submit succeeded; only the body is unreadable. Not worth failing.
    warn('the form endpoint returned an unreadable body after a successful submit', {
      reason: describeError(e),
    });
    return {};
  }
}
